import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Trash2, AlertTriangle, RefreshCw, FileInput, Info, FileText, Boxes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const InwardEntryManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('document_barcode');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // Entry list
  const [entries, setEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [listSearchTerm, setListSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const limit = 20;
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteSearchTerm, setDeleteSearchTerm] = useState('');
  const [deleteSearchType, setDeleteSearchType] = useState('document_barcode');
  const [deleteRelated, setDeleteRelated] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchEntries = async () => {
    setIsLoadingEntries(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (listSearchTerm) params.append('search', listSearchTerm);
      
      const response = await apiClient.get(`/admin/inward-entries/list?${params}`);
      setEntries(response.data.entries || []);
      setTotalEntries(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load Inward entries');
    } finally {
      setIsLoadingEntries(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [page]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    
    setIsSearching(true);
    setSearchResults(null);
    try {
      const param = searchType === 'document_barcode' ? 'document_barcode' : 'entry_id';
      const response = await apiClient.get(`/admin/inward-entries/search?${param}=${encodeURIComponent(searchTerm.trim())}`);
      setSearchResults(response.data);
      
      if (!response.data.found) {
        toast.info('No Inward data found for this search term');
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const openDeleteDialog = (term, type = 'document_barcode') => {
    setDeleteSearchTerm(term);
    setDeleteSearchType(type);
    setDeleteRelated(true);
    setConfirmText('');
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setIsDeleting(true);
    try {
      const param = deleteSearchType === 'document_barcode' ? 'document_barcode' : 'entry_id';
      const response = await apiClient.delete(
        `/admin/inward-entries?${param}=${encodeURIComponent(deleteSearchTerm)}&delete_related=${deleteRelated}`
      );
      
      toast.success(`Deleted ${response.data.total_records_deleted} records`);
      setShowDeleteDialog(false);
      setSearchResults(null);
      setSearchTerm('');
      fetchEntries();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleListSearch = () => {
    setPage(0);
    fetchEntries();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="inward-entry-management">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <FileInput className="w-6 h-6" />
                Inward Entry Management
              </h1>
              <p className="text-sm text-[#E8DCC8]">Delete Inward entries and related data</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {/* Search */}
        <Card className="border-[#E5DFD0]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1A1F2E]">
              <Search className="w-5 h-5" />
              Search Entry
            </CardTitle>
            <CardDescription>
              Search by document barcode or entry ID to view and delete Inward data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 flex-wrap">
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="px-3 py-2 border rounded-md bg-white"
              >
                <option value="document_barcode">Document Barcode</option>
                <option value="entry_id">Entry ID</option>
              </select>
              <Input
                placeholder={searchType === 'document_barcode' ? 'Enter Document Barcode (e.g., DOC000001/25-26)' : 'Enter Entry ID'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 min-w-[300px]"
                data-testid="search-input"
              />
              <Button 
                onClick={handleSearch} 
                disabled={isSearching}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="search-btn"
              >
                {isSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                <span className="ml-2">Search</span>
              </Button>
            </div>

            {/* Search Results */}
            {searchResults && (
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#1A1F2E]">
                    Results for: {searchResults.search_term}
                  </h3>
                  {searchResults.found && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => openDeleteDialog(searchResults.search_term, searchResults.search_type)}
                      data-testid="delete-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete All Data
                    </Button>
                  )}
                </div>

                {searchResults.found ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Inward Entries */}
                    <Card className={searchResults.data.inward_entries ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-gray-50'}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FileInput className="w-5 h-5 text-emerald-600" />
                          <h4 className="font-semibold">Inward Entries</h4>
                        </div>
                        {searchResults.data.inward_entries ? (
                          <div>
                            <p className="text-2xl font-bold text-emerald-600">{searchResults.data.inward_entries.count}</p>
                            {searchResults.data.inward_entries.entries?.length > 0 && (
                              <p className="text-xs text-gray-600 mt-1">
                                Latest: {searchResults.data.inward_entries.entries[0]?.vendor_name || '-'}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Documentations */}
                    <Card className={searchResults.data.documentations ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <h4 className="font-semibold">Documentations</h4>
                        </div>
                        {searchResults.data.documentations ? (
                          <p className="text-2xl font-bold text-blue-600">{searchResults.data.documentations.count}</p>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Consignments */}
                    <Card className={searchResults.data.consignments ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-gray-50'}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Boxes className="w-5 h-5 text-purple-600" />
                          <h4 className="font-semibold">Consignments</h4>
                        </div>
                        {searchResults.data.consignments ? (
                          <div>
                            <p className="text-2xl font-bold text-purple-600">{searchResults.data.consignments.count}</p>
                            {searchResults.data.consignments.consignments?.length > 0 && (
                              <p className="text-xs text-gray-600 mt-1">
                                Sections: {searchResults.data.consignments.consignments.map(c => c.section_name).join(', ')}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-100 rounded-lg text-center">
                    <Info className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">No Inward data found</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Entry List */}
        <Card className="border-[#E5DFD0]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-[#1A1F2E]">Recent Inward Entries</CardTitle>
                <CardDescription>Latest inward gate entries</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchEntries} disabled={isLoadingEntries}>
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingEntries ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 mb-4">
              <Input
                placeholder="Search by entry ID, document barcode, vendor name..."
                value={listSearchTerm}
                onChange={(e) => setListSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleListSearch()}
                className="flex-1"
              />
              <Button onClick={handleListSearch} variant="outline">
                <Search className="w-4 h-4" />
              </Button>
            </div>

            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entry ID</TableHead>
                    <TableHead>Document Barcode</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingEntries ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-emerald-600" />
                      </TableCell>
                    </TableRow>
                  ) : entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No entries found
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((entry, idx) => (
                      <TableRow key={`${entry.entry_id}-${idx}`}>
                        <TableCell className="font-mono text-sm">{entry.entry_id || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.document_barcode || '-'}</TableCell>
                        <TableCell className="text-sm">{entry.vendor_name || '-'}</TableCell>
                        <TableCell className="text-sm">{entry.vehicle_number || '-'}</TableCell>
                        <TableCell className="text-sm text-gray-600">{formatDate(entry.created_at)}</TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => openDeleteDialog(entry.entry_id, 'entry_id')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {totalEntries > limit && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-600">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, totalEntries)} of {totalEntries}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= totalEntries}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Delete Inward Entry Data
            </DialogTitle>
            <DialogDescription>
              This will permanently delete Inward data for:
              <span className="font-mono font-bold block mt-1">{deleteSearchTerm}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="delete-related"
                checked={deleteRelated}
                onCheckedChange={(checked) => setDeleteRelated(checked)}
              />
              <label htmlFor="delete-related" className="text-sm">
                Also delete related documentations and consignments
              </label>
            </div>

            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> If this entry has been assigned to FOB or other sections, 
                deleting it may affect those operations.
              </p>
            </div>

            <div>
              <Label className="text-red-700">Type DELETE to confirm:</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="mt-1 border-red-300 focus:border-red-500"
                data-testid="confirm-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting || confirmText !== 'DELETE'}
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InwardEntryManagement;
